import { Button, ProfileCard, Spinner, Text } from '@change/ds-core-ui';
import { DecisionMakerIcon } from '@change/ds-icons';

import { Translate, TranslatePlural } from '@change-corgi/core-react/i18n';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { useDecisionMakersSection } from './hooks/useDecisionMakersSection';

export const DecisionMakersSection: React.FC = () => {
	const {
		data: { displayed, displayName, decisionMakers, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = useDecisionMakersSection();

	const { containerRef, loadingRef, recordListEvent, lastEvent, loadMoreRef, getListItemRef } =
		useLazyLoadedListFocus();

	if (!displayed) {
		return null;
	}

	return (
		<div className="flex flex-col" data-qa="decision-makers-container">
			<Text
				as="h2"
				variant="heading"
				size="small"
				className="text-typography-strong mb-6"
				data-qa="decision-makers-heading"
			>
				<Translate value="corgi.local.decision_makers.heading" replacements={{ displayName }} />
			</Text>

			<div ref={containerRef} className="flex flex-col gap-6">
				<div className="grid grid-flow-row grid-cols-1 place-items-center gap-[26px] md:w-full md:grid-cols-4">
					{[
						decisionMakers.map((decisionMaker, index) => {
							return (
								<div className="flex h-full justify-center justify-self-stretch" key={decisionMaker.id}>
									{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
										<div className="sr-only" role="alert">
											<TranslatePlural
												value="corgi.local.decision_makers.fetched_more"
												count={decisionMakers.length - lastEvent.previousItemsCount}
												replacements={{ count: decisionMakers.length - lastEvent.previousItemsCount }}
											/>
										</div>
									)}

									<div className="w-full" ref={getListItemRef(index)}>
										<ProfileCard
											data-qa="decision-maker-petition-card"
											className="m-auto h-full w-full"
											href={decisionMaker.publiclyVisible ? `/decision-makers/${decisionMaker.slug}` : undefined}
											name={decisionMaker.displayName}
											userRole={decisionMaker.title}
											avatar={decisionMaker.photo?.userMedium.url}
											nameClassName="truncate w-full"
											icon={<DecisionMakerIcon />}
											iconPosition="center"
										/>
									</div>
								</div>
							);
						}),
					]}
				</div>

				{nextPageLoading && (
					<>
						<div className="sr-only" role="alert">
							<Translate value="corgi.local.decision_makers.fetching_more" />
						</div>
						<Spinner className="mx-auto mt-6" ref={loadingRef} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						className="max-w-[280px] self-center max-md:w-full!"
						data-qa="decision-makers-load-more-button"
						data-testid="decision-makers-load-more-button"
						variant="secondary"
						size="small"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: decisionMakers.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.local.decision_makers.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
